import React, { useState } from 'react';
import { hapticSelection } from '../utils/haptics';
import { formatDriverName } from '../utils/driverName';
import { APP } from '../config/app';
import useScrollLock from '../utils/useScrollLock';
import PhotoLightbox from './PhotoLightbox';
import VehicleSafetyReport from './VehicleSafetyReport';

export default function DriverProfileModal({
  driver,
  ride,
  onClose,
  t = (k) => k,
  showSafetyReport = false,
}) {
  useScrollLock(Boolean(driver || ride));
  const [photoFailed, setPhotoFailed] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(false);
  if (!driver && !ride) return null;

  const rawName = (driver?.name || ride?.driverName || '').trim();
  const displayName = formatDriverName(rawName);
  const initial = rawName.replace(/^(Anna|Captain)\s+/i, '').trim().split(/\s+/)[0]?.charAt(0).toUpperCase() || 'A';
  const photoUrl = driver?.photoUrl || ride?.driverPhoto;
  const photo = photoUrl && !photoFailed ? photoUrl : null;

  const rawRating = driver?.ratingAvg ?? ride?.driverRatingAvg ?? driver?.rating ?? ride?.driverRating;
  const ratingCount = Number(driver?.ratingCount ?? ride?.driverRatingCount ?? 0);
  const hasRating = Number.isFinite(Number(rawRating)) && Number(rawRating) > 0;
  const ratingVal = hasRating ? Number(rawRating) : null;

  const totalRidesVal = driver?.totalRides ?? driver?.totalCompletedRides ?? driver?.lifetimeRides ?? ride?.driverTotalRides ?? ride?.totalCompletedRides ?? ride?.lifetimeRides ?? driver?.completedRides ?? ride?.driverCompletedRides;
  const totalRides = Number.isFinite(Number(totalRidesVal)) ? Number(totalRidesVal) : 0;

  const isApproved = driver?.isApproved === true || ride?.driverApproved === true || Boolean(driver?.profileComplete);

  let plate = (driver?.vehicleNumber || ride?.vehicleNumber || '').trim();
  plate = plate.replace(/^Auto\s*•\s*/i, '').trim();

  let vehicleModel = (driver?.vehicleModel || ride?.vehicleModel || driver?.vehicle || '').trim();
  if (vehicleModel.toLowerCase().includes('rickshaw')) {
    vehicleModel = vehicleModel.replace(/\s*Rickshaw\b/gi, '').trim();
  }

  return (
    <div className="modal-backdrop fade-in" onClick={onClose}>
      <div
        className="modal-card driver-profile-modal slide-up"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={t('driver.annaProfile')}
      >
        {/* Header */}
        <div className="driver-modal-header">
          <h3>{t('driver.annaProfile')}</h3>
          <button
            type="button"
            className="driver-modal-close-btn"
            onClick={() => {
              hapticSelection();
              onClose();
            }}
            aria-label={t('common.close')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Hero Section */}
        <div className="driver-modal-hero">
          <div className="driver-modal-avatar-wrap">
            {photo ? (
              <button
                type="button"
                className="driver-modal-avatar-btn"
                onClick={() => setPhotoPreview(true)}
                aria-label={t('driver.viewPhoto') || 'View photo'}
              >
                <img
                  className="driver-modal-avatar-img"
                  src={photo}
                  alt={displayName}
                  onError={() => setPhotoFailed(true)}
                />
              </button>
            ) : (
              <div className="driver-modal-avatar-circle" aria-hidden>{initial}</div>
            )}
            {isApproved && (
              <div className="driver-modal-verified-badge" title={t('driver.verified')}>
                ✓
              </div>
            )}
          </div>

          <h2 className="driver-modal-name">{displayName}</h2>
          <span className="driver-modal-status-badge">
            <span className={isApproved ? 'verified-dot' : 'unverified-dot'} />
            {isApproved ? t('driver.verifiedPartner') : (t('driver.communityCaptain') || 'Registered Anna')}
          </span>
        </div>

        {/* Stats Grid */}
        <div className="driver-modal-stats-grid">
          <div className="driver-stat-card">
            <span className="stat-value star">
              {ratingVal != null ? `★ ${ratingVal.toFixed(1)}` : `★ ${t('driver.newRating') || 'New'}`}
            </span>
            <span className="stat-label">
              {ratingVal != null && ratingCount > 0
                ? t('driver.ratingWithCount', { n: ratingCount })
                : t('driver.rating')}
            </span>
          </div>

          <div className="driver-stat-card">
            <span className="stat-value">
                {totalRides > 0
                ? t('driver.tripsCount', { n: totalRides.toLocaleString() })
                : '—'}
            </span>
            <span className="stat-label">{t('driver.experience')}</span>
          </div>

          <div className="driver-stat-card">
            <span className="stat-value">{APP.city}</span>
            <span className="stat-label">{t('driver.operatingArea')}</span>
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="driver-modal-section">
          <h4 className="driver-section-title">{t('driver.vehicleDetails')}</h4>
          <div className="driver-vehicle-info-card">
            <div className="vehicle-info-icon" aria-hidden>🛺</div>
            <div className="vehicle-info-text">
              <span className="vehicle-info-plate">{plate || t('driver.autoFallback')}</span>
              {vehicleModel && <span className="vehicle-info-model">{vehicleModel}</span>}
            </div>
          </div>
        </div>

        {/* Auto Safety Report */}
        {(showSafetyReport || ride?.isIntertown === true) && (
          <div className="driver-modal-section">
            <VehicleSafetyReport driver={driver} ride={ride} t={t} />
          </div>
        )}

        {/* Verified Credentials */}
        {(driver?.identityVerified === true || driver?.vehicleVerified === true) && (
          <div className="driver-modal-section">
            <h4 className="driver-section-title">{t('driver.credentials')}</h4>
            <div className="driver-badges-list">
              {driver?.identityVerified === true && <div className="driver-badge-item"><span className="badge-ico">🪪</span><div className="badge-text"><strong>{t('driver.credIdentity')}</strong><span>{t('driver.credIdentitySub')}</span></div></div>}
              {driver?.vehicleVerified === true && <div className="driver-badge-item"><span className="badge-ico">🛺</span><div className="badge-text"><strong>{t('driver.credAuto')}</strong><span>{t('driver.credAutoSub', { city: APP.city })}</span></div></div>}
            </div>
          </div>
        )}

        {/* Bottom Button */}
        <button
          type="button"
          className="btn btn-primary btn-block driver-modal-close-cta"
          onClick={() => {
            hapticSelection();
            onClose();
          }}
        >
          {t('common.done')}
        </button>
      </div>

      {photoPreview && photo && (
        <PhotoLightbox
          src={photo}
          alt={displayName}
          onClose={() => setPhotoPreview(false)}
          closeLabel={t('common.close')}
        />
      )}
    </div>
  );
}
